import { create } from 'zustand';

/**
 * Schedule execution status
 */
export type ExecutionStatus = 'pending' | 'success' | 'failed' | 'timeout';

/**
 * Session mode for schedule executions
 */
export type ScheduleSessionMode = 'ephemeral' | 'dedicated' | 'inherit' | 'fixed';

/**
 * Output channel for schedule results
 */
export interface PlatformOutputChannel {
    type: 'platform';
    platform: 'telegram' | 'slack' | 'discord';
    channelId: string;
    threadId?: string;
}

export interface NoOutputChannel {
    type: 'none';
}

export type OutputChannel = PlatformOutputChannel | NoOutputChannel;

/**
 * Schedule interface matching dexto-multi-agent-server backend
 */
export interface Schedule {
    id: string;
    name: string;
    cronExpression: string;
    timezone: string;
    enabled: boolean;
    task: {
        /** Instruction for the executing agent */
        instruction: string;
        metadata?: Record<string, unknown>;
    };
    /** How session context is managed for executions */
    sessionMode: ScheduleSessionMode;
    /** Session ID for 'fixed' mode, or captured session for 'inherit' mode */
    sessionId?: string;
    /** Where to route the execution result */
    outputTo?: OutputChannel;
    createdAt: number;
    updatedAt: number;
    lastRunAt?: number;
    nextRunAt?: number;
    runCount: number;
    successCount: number;
    failureCount: number;
    lastError?: string;
}

/**
 * Execution log for a schedule run
 */
export interface ExecutionLog {
    id: string;
    scheduleId: string;
    triggeredAt: number;
    completedAt?: number;
    status: ExecutionStatus;
    duration?: number;
    error?: string;
    result?: string;
    sessionId?: string;
}

/**
 * Create schedule input - matches server's CreateScheduleInputSchema
 */
export interface CreateScheduleInput {
    name: string;
    cronExpression: string;
    instruction: string;
    timezone?: string;
    enabled?: boolean;
    sessionMode?: ScheduleSessionMode;
    sessionId?: string;
    metadata?: Record<string, unknown>;
    outputTo?: OutputChannel;
}

/**
 * Update schedule input - matches server's UpdateScheduleInputSchema
 */
export interface UpdateScheduleInput {
    name?: string;
    cronExpression?: string;
    instruction?: string;
    timezone?: string;
    enabled?: boolean;
    sessionMode?: ScheduleSessionMode;
    sessionId?: string;
    metadata?: Record<string, unknown>;
    outputTo?: OutputChannel;
}

/**
 * Scheduler status
 */
export interface SchedulerStatus {
    initialized: boolean;
    started: boolean;
    activeSchedules: number;
    totalSchedules: number;
}

interface SchedulerStore {
    // State
    schedules: Map<string, Schedule>;
    executionLogs: Map<string, ExecutionLog[]>; // scheduleId -> logs
    runningScheduleIds: Set<string>;
    schedulerStatus: SchedulerStatus | null;
    isLoading: boolean;
    error: string | null;

    // Actions
    setSchedules: (schedules: Schedule[]) => void;
    addSchedule: (schedule: Schedule) => void;
    updateSchedule: (scheduleId: string, updates: Partial<Schedule>) => void;
    removeSchedule: (scheduleId: string) => void;

    // Execution state
    setScheduleRunning: (scheduleId: string, running: boolean) => void;
    addExecutionLog: (log: ExecutionLog) => void;
    setExecutionLogs: (scheduleId: string, logs: ExecutionLog[]) => void;

    // Status
    setSchedulerStatus: (status: SchedulerStatus) => void;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;

    // Getters
    getSchedule: (scheduleId: string) => Schedule | undefined;
    getEnabledSchedules: () => Schedule[];
    getDisabledSchedules: () => Schedule[];
    getRunningSchedules: () => Schedule[];
    getFailingSchedules: () => Schedule[];
    getScheduleExecutionLogs: (scheduleId: string) => ExecutionLog[];
    getActiveCount: () => number;
    getRunningCount: () => number;

    // Clear
    clearSchedules: () => void;
}

export const useSchedulerStore = create<SchedulerStore>((set, get) => ({
    // Initial state
    schedules: new Map(),
    executionLogs: new Map(),
    runningScheduleIds: new Set(),
    schedulerStatus: null,
    isLoading: false,
    error: null,

    // Actions
    setSchedules: (schedules) => {
        const scheduleMap = new Map<string, Schedule>();
        schedules.forEach((s) => scheduleMap.set(s.id, s));
        set({ schedules: scheduleMap, isLoading: false });
    },

    addSchedule: (schedule) => {
        set((state) => {
            const newSchedules = new Map(state.schedules);
            newSchedules.set(schedule.id, schedule);
            return { schedules: newSchedules };
        });
    },

    updateSchedule: (scheduleId, updates) => {
        set((state) => {
            const newSchedules = new Map(state.schedules);
            const existing = newSchedules.get(scheduleId);
            if (existing) {
                newSchedules.set(scheduleId, { ...existing, ...updates, updatedAt: Date.now() });
            }
            return { schedules: newSchedules };
        });
    },

    removeSchedule: (scheduleId) => {
        set((state) => {
            const newSchedules = new Map(state.schedules);
            newSchedules.delete(scheduleId);
            const newLogs = new Map(state.executionLogs);
            newLogs.delete(scheduleId);
            const newRunning = new Set(state.runningScheduleIds);
            newRunning.delete(scheduleId);
            return {
                schedules: newSchedules,
                executionLogs: newLogs,
                runningScheduleIds: newRunning
            };
        });
    },

    // Execution state
    setScheduleRunning: (scheduleId, running) => {
        set((state) => {
            const newRunning = new Set(state.runningScheduleIds);
            if (running) {
                newRunning.add(scheduleId);
            } else {
                newRunning.delete(scheduleId);
            }
            return { runningScheduleIds: newRunning };
        });
    },

    addExecutionLog: (log) => {
        set((state) => {
            const newLogs = new Map(state.executionLogs);
            const existingLogs = newLogs.get(log.scheduleId) || [];
            // Add new log at the beginning (most recent first)
            newLogs.set(log.scheduleId, [log, ...existingLogs].slice(0, 100)); // Keep last 100
            return { executionLogs: newLogs };
        });
    },

    setExecutionLogs: (scheduleId, logs) => {
        set((state) => {
            const newLogs = new Map(state.executionLogs);
            newLogs.set(scheduleId, logs);
            return { executionLogs: newLogs };
        });
    },

    // Status
    setSchedulerStatus: (status) => {
        set({ schedulerStatus: status });
    },

    setLoading: (loading) => {
        set({ isLoading: loading });
    },

    setError: (error) => {
        set({ error });
    },

    // Getters
    getSchedule: (scheduleId) => {
        return get().schedules.get(scheduleId);
    },

    getEnabledSchedules: () => {
        const { schedules } = get();
        return Array.from(schedules.values())
            .filter((s) => s.enabled)
            .sort((a, b) => (b.nextRunAt || 0) - (a.nextRunAt || 0));
    },

    getDisabledSchedules: () => {
        const { schedules } = get();
        return Array.from(schedules.values())
            .filter((s) => !s.enabled)
            .sort((a, b) => b.updatedAt - a.updatedAt);
    },

    getRunningSchedules: () => {
        const { schedules, runningScheduleIds } = get();
        return Array.from(schedules.values())
            .filter((s) => runningScheduleIds.has(s.id))
            .sort((a, b) => b.updatedAt - a.updatedAt);
    },

    getFailingSchedules: () => {
        const { schedules } = get();
        return Array.from(schedules.values())
            .filter((s) => s.lastError && s.failureCount > 0)
            .sort((a, b) => b.failureCount - a.failureCount);
    },

    getScheduleExecutionLogs: (scheduleId) => {
        return get().executionLogs.get(scheduleId) || [];
    },

    getActiveCount: () => {
        const { schedules } = get();
        return Array.from(schedules.values()).filter((s) => s.enabled).length;
    },

    getRunningCount: () => {
        return get().runningScheduleIds.size;
    },

    // Clear
    clearSchedules: () => {
        set({
            schedules: new Map(),
            executionLogs: new Map(),
            runningScheduleIds: new Set(),
            schedulerStatus: null,
            error: null
        });
    },
}));
